import {compact} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  ProcessExecutionType,
  ProcessTaskExecutionOriginType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {UpdateTaskParams} from 'processes/process-task-details/utils/updateTaskDetailsTask';
import {creationOriginTypeToDisplayName} from 'processes/ProcessConstants';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import ProcessChip from 'ui-library/chips/ProcessChip';
import ProcessTaskChip from 'ui-library/chips/ProcessTaskChip';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import EditablePartyField from 'ui-library/inputs/EditablePartyField';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import * as DateUtil from 'util/DateUtil';

import AssigneeField from '../fields/AssigneeField';
import FormattedTextField from '../fields/FormattedTextField';
import getRequestV2SubmissionRowContent from '../fields/getRequestV2SubmissionRowContent';
import getStudiesRowContent from '../fields/getStudiesRowContent';
import PlaceholderFieldValue from '../fields/PlaceholderFieldValue';
import {ScheduledOnField} from '../fields/ScheduledOnField';
import SourceTasksAndOutputsField from '../fields/SourceTasksAndOutputsField';
import StatusField from '../fields/StatusField';
import {
  ProcessTaskDetailsRowContent,
  ProcessTaskDetailsSections,
  PROCESS_TASK_DETAILS_PAGE_STYLES,
  getProcessTaskDetailsLabels,
  ProcessTaskGql,
} from '../ProcessTaskDetailsPageConstants';

export function getProcessTaskDetailsMetadataRows(
  processTask: ProcessTaskGql
): Array<ProcessTaskDetailsRowContent> {
  const processTaskDetailsLabels = getProcessTaskDetailsLabels();
  return compact([
    {
      label: processTaskDetailsLabels.CREATOR,
      component: (
        <AvatarWithName
          avatarInfo={processTask.creator}
          size={AvatarSize.XXSMALL}
          shape={AvatarShape.CIRCLE}
          showTooltip={true}
        />
      ),
    },
    {
      label: processTaskDetailsLabels.CREATED,
      component: <FormattedTextField text={DateUtil.readableTime(processTask.createdAt)} />,
    },
    {
      label: processTaskDetailsLabels.CREATION_ORIGIN,
      component: (
        <>
          {processTask.creationOrigin.originType != null ? (
            <FormattedTextField
              text={creationOriginTypeToDisplayName[processTask.creationOrigin.originType]}
            />
          ) : (
            <PlaceholderFieldValue />
          )}
        </>
      ),
    },
    {
      label: processTaskDetailsLabels.MODIFIED,
      component: (
        <span className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.rowHeight)}>
          {DateUtil.readableTime(processTask.modifiedAt)}
        </span>
      ),
    },
    {
      label: processTaskDetailsLabels.SOURCES,
      component: <SourceTasksAndOutputsField processTask={processTask} />,
    },
    {
      label: processTaskDetailsLabels.FLOWCHART_TASK,
      component: (
        <>
          {(() => {
            if (processTask.rootTasks.length > 0) {
              return processTask.rootTasks.map((rootTask) => (
                <ProcessTaskChip
                  key={rootTask.id}
                  className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.chipHeight)}
                  id={rootTask.id}
                  size={ChipSize.TABLE_CELL}
                />
              ));
            } else if (processTask.schema.executionType === ProcessExecutionType.FLOWCHART) {
              return <FormattedTextField text="(This is the flowchart task)" />;
            } else {
              return <PlaceholderFieldValue />;
            }
          })()}
        </>
      ),
    },
    {
      label: processTaskDetailsLabels.TASK_GROUP,
      component: (
        <ProcessChip
          className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.chipHeight)}
          id={processTask.process.id}
          size={ChipSize.TABLE_CELL}
        />
      ),
    },
    BenchlingConfig.STUDIES_ENABLE ? getStudiesRowContent(processTask) : null,
  ]);
}

export function getProcessTaskDetailsSummaryRows(
  processTask: ProcessTaskGql,
  onUpdateTask: (updatedTask: UpdateTaskParams) => Promise<void>
): Array<ProcessTaskDetailsRowContent> {
  const processTaskDetailsLabels = getProcessTaskDetailsLabels();
  return compact([
    {
      label: processTaskDetailsLabels.STATUS,
      component: <StatusField processTask={processTask} onUpdateTask={onUpdateTask} />,
    },
    {
      label: processTaskDetailsLabels.ASSIGNEE,
      component: <AssigneeField processTask={processTask} onUpdateTask={onUpdateTask} />,
    },
    {
      label: processTaskDetailsLabels.SCHEDULED_ON,
      component: (
        <>
          {processTask.schema.executionType !== ProcessExecutionType.FLOWCHART ? (
            <ScheduledOnField processTask={processTask} onUpdateTask={onUpdateTask} />
          ) : (
            <PlaceholderFieldValue />
          )}
        </>
      ),
    },
    {
      label: processTaskDetailsLabels.RESPONSIBLE_PARTIES,
      component: (
        <EditablePartyField
          value={processTask.responsibleParties}
          onChange={async (parties: Array<MinimalEntity>) => {
            await onUpdateTask({
              id: processTask.id,
              responsiblePartiesIds: parties.map(({id}) => id),
            });
          }}
          isDisabled={false}
          chipSize={TagsInputSize.SMALL}
          enableNewEntityInput={true}
          allowEmails={false}
          allowTeams={true}
          allowOrganizations={false}
          shouldAllowMultiple={true}
          userSearchType={UserSearchType.NotSuspended}
          isFullWidth={true}
          initialIsBeingEdited={false}
          allowEmptyValue={true}
          shouldUseCompactDisplay={true}
          useAvatars={true}
          maxAvatars={2}
        />
      ),
    },
    {
      label: processTaskDetailsLabels.EXECUTED_BY,
      component: (
        <>
          {processTask.executor != null ? (
            <AvatarWithName
              avatarInfo={processTask.executor}
              size={AvatarSize.XXSMALL}
              shape={AvatarShape.CIRCLE}
              showTooltip={false}
            />
          ) : (
            <PlaceholderFieldValue />
          )}
        </>
      ),
    },
    {
      label: processTaskDetailsLabels.EXECUTED_IN,
      component: (
        <>
          {(() => {
            if (processTask.executionOrigin?.executionEntry != null) {
              return (
                <EntryChip
                  className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.chipHeight)}
                  id={processTask.executionOrigin.executionEntry.id}
                  size={ChipSize.TABLE_CELL}
                />
              );
            } else if (processTask.executionOrigin?.executionType === ProcessTaskExecutionOriginType.MODAL) {
              return <FormattedTextField text="Direct execution pop-up window" />;
            } else {
              return <PlaceholderFieldValue />;
            }
          })()}
        </>
      ),
    },
    {
      label: processTaskDetailsLabels.EXECUTED_ON,
      component: (
        <>
          {processTask.executionOrigin != null ? (
            <FormattedTextField text={DateUtil.readableTime(processTask.executionOrigin.createdAt)} />
          ) : (
            <PlaceholderFieldValue />
          )}
        </>
      ),
    },
    BenchlingConfig.REQUESTS_V2_ENABLE ? getRequestV2SubmissionRowContent(processTask) : null,
    {
      label: processTaskDetailsLabels.SCHEMA,
      component: (
        <span className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.rowHeight)}>{processTask.schema.name}</span>
      ),
    },
  ]);
}

export default function getProcessTaskDetailsSections(
  processTask: ProcessTaskGql,
  onUpdateTask: (updatedTask: UpdateTaskParams) => Promise<void>
): ProcessTaskDetailsSections {
  return {
    summary: getProcessTaskDetailsSummaryRows(processTask, onUpdateTask),
    metadata: getProcessTaskDetailsMetadataRows(processTask),
  };
}
